/**
 * Root error boundary — catches render-phase crashes that would otherwise
 * leave the admin app stuck on a blank black page. Shows the actual error
 * + a "reload" button so the operator can recover without DevTools.
 */

import React from 'react';

interface State { error: Error | null }

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[admin] root error boundary:', error, info);
  }

  reset = () => {
    this.setState({ error: null });
    try { localStorage.removeItem('supabase.auth.token'); } catch {}
    window.location.href = '/login';
  };

  render() {
    if (!this.state.error) return this.props.children;
    const e = this.state.error;
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0A2540',
        color: '#e0e0e0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>⚠️</div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 22, color: '#fff' }}>
            Admin failed to start
          </h1>
          <p style={{ color: '#94a3b8', margin: '0 0 16px 0', fontSize: 14 }}>
            Something crashed during the initial render. The original error is below.
          </p>
          <pre style={{
            background: 'rgba(255,255,255,0.06)',
            color: '#fbbf24',
            padding: 12,
            borderRadius: 8,
            fontSize: 12,
            textAlign: 'left',
            overflow: 'auto',
            maxHeight: 220,
          }}>
            {e.name}: {e.message}{'\n'}
            {e.stack ? e.stack.split('\n').slice(0, 6).join('\n') : ''}
          </pre>
          <button
            onClick={this.reset}
            style={{
              marginTop: 16,
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
              color: '#0A2540',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Clear session and reload
          </button>
        </div>
      </div>
    );
  }
}
